/**
 * InfoHint - an explanation you open on purpose, not a wall of text.
 *
 * Operator, 2026-09-29: *"i always prefer info and errors to be an
 * intentional read, not just a wall of text. so put the info behind a click
 * popup modal."* Agreed with one condition, which is the reason `short`
 * exists: the NEXT STEP stays visible. A novice who sees an empty picker and
 * only an (i) has to guess that the answer is behind it, which fails the
 * suite's "i wanna do this" bar (SUITE-FACTS s0). So:
 *
 *   - `short` - at most one short line, shown ONLY when something needs
 *     doing, naming what to do ("No programs yet - add one in OPBridge").
 *   - the button - opens the full explanation in a modal. Labelled in WORDS
 *     ("Why?" / "What this does"), never a bare glyph to decode.
 *   - omit `short` for pure explanation of what a control does; then only the
 *     button shows.
 *
 * Short one-liners that are already the whole message ("No program chosen -
 * this button does nothing yet.") stay plain `.inspector-hint` text; wrapping
 * them would add a tap to read what is already readable.
 *
 * The modal reuses the confirm chassis (`confirm-overlay` / `confirm-modal`),
 * sized in % so it fits the frame in both orientations. Esc and a backdrop
 * tap close it.
 *
 * ⚠️ MOUNTED ON `body`, NOT `shellPortal` - a deliberate departure from the
 * modal lock, for a reason the lock did not foresee. InfoHints open FROM the
 * Inspector, and the Inspector lives OUTSIDE `.shell` (z-overlay 100). `.shell`
 * carries a transform, so everything mounted inside it stacks at the shell's
 * own level and can never cover the Inspector, whatever its z-index. Measured
 * 2026-09-29 with `elementFromPoint` on "Got it": under the Inspector's
 * backdrop in landscape, and under the Inspector PANEL in a true 820x1180
 * portrait frame. Hiding the backdrop fixed only the first. Mounting inside
 * the panel is out too: the panel has a transform, so a fixed overlay there
 * would be trapped in the panel's box. Same answer the Inspector's own
 * MatrixPicker already uses (a bare `<Portal>`). On the iPad the page IS the
 * frame, so it still fits; `info_hint_opens_on_purpose` checks both
 * orientations.
 */

import { createSignal, onCleanup, Show, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';

export interface InfoHintProps {
  /** Modal title - what this is about, in a few words. */
  title: string;
  /** The one visible line. Omit for pure "what does this do" info. */
  short?: JSX.Element;
  /** Button wording. Default: "Why?" with a `short`, "What this does"
   *  without one. */
  label?: string;
  /** The full explanation, shown in the modal. */
  children: JSX.Element;
}

export function InfoHint(props: InfoHintProps) {
  const [open, setOpen] = createSignal(false);
  const label = () => props.label ?? (props.short !== undefined ? 'Why?' : 'What this does');

  // Capture phase + stopPropagation, the ItemContextMenu precedent: "one
  // Escape, one thing". Without it App's window Escape handler ALSO ran and
  // deselected the item, closing the Inspector behind the modal - seen in
  // `info_hint_opens_on_purpose` on its first run.
  function onKey(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    close();
  }
  const openModal = () => {
    if (open()) return;
    setOpen(true);
    window.addEventListener('keydown', onKey, true);
  };
  function close(): void {
    if (!open()) return;
    setOpen(false);
    window.removeEventListener('keydown', onKey, true);
  }
  // Unmounting while open (the item deselected under it) drops the listener.
  onCleanup(() => close());

  return (
    <div class="info-hint" data-has-short={props.short !== undefined ? 'true' : 'false'}>
      <Show when={props.short !== undefined}>
        <span class="inspector-hint info-hint-short">{props.short}</span>
      </Show>
      <button
        type="button"
        class="info-hint-btn"
        onClick={openModal}
        aria-haspopup="dialog"
        aria-label={`${label()} - ${props.title}`}
      >
        <span class="info-hint-glyph" aria-hidden="true">i</span>
        {label()}
      </button>
      <Show when={open()}>
        <Portal>
          <div
            class="confirm-overlay info-overlay"
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-label={props.title}
          >
            <div class="confirm-modal info-modal" onClick={(e) => e.stopPropagation()}>
              <div class="confirm-title">{props.title}</div>
              <div class="confirm-message info-modal-body">{props.children}</div>
              <button type="button" class="confirm-choice-button info-modal-close" data-tone="neutral" onClick={close}>
                <span class="confirm-choice-label">Got it</span>
              </button>
            </div>
          </div>
        </Portal>
      </Show>
    </div>
  );
}
